import {
  ChartColumn,
  ChevronRight,
  Cpu,
  Earth,
  FolderKanban,
  FolderTree,
  Gauge,
  KeyRound,
  LifeBuoy,
  LockKeyhole,
  type LucideIcon,
  Network,
  NotebookPen,
  Palette,
  SlidersHorizontal,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/EmptyState";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import type { BackofficeSection, SettingsGroup } from "../router.tsx";
import { adminSettingsPath, Link, SETTINGS_GROUPS } from "../router.tsx";
import { AuditList } from "./AuditList.tsx";
import { useBackofficeAccess } from "./access.ts";
import { useAdminApi } from "./admin-api.tsx";
import { BrandingScreen } from "./BrandingScreen.tsx";
import { DirectoryAdmin } from "./DirectoryAdmin.tsx";
import { EntitiesAdmin } from "./EntitiesAdmin.tsx";
import { GovernedProjects } from "./GovernedProjects.tsx";
import { GovernedProjectView } from "./GovernedProjectView.tsx";
import { MemoryScreen } from "./MemoryScreen.tsx";
import { ModelsScreen } from "./ModelsScreen.tsx";
import { PlanSection } from "./PlanSection.tsx";
import { ResidencyScreen } from "./ResidencyScreen.tsx";
import { Reports } from "./reports/Reports.tsx";
import { SettingsAdmin } from "./SettingsAdmin.tsx";
import { SignInAdmin } from "./SignInAdmin.tsx";
import { SupportSection } from "./SupportSection.tsx";
import { UsersAdmin } from "./UsersAdmin.tsx";

/**
 * The tenant administration area. Hiding it from members is presentation only: every backoffice
 * route checks the role on the server.
 */
interface BackofficeProps {
  section: BackofficeSection | null;
  /** One governed project, under the `projects` section. */
  projectId?: string | undefined;
  /** One settings page, under the `settings` section (none: the first, by redirect). */
  settingsGroup?: SettingsGroup | undefined;
}

export function Backoffice(props: BackofficeProps) {
  const { readOnly } = useAdminApi();
  // Under a support grant the tenant approved access: the server decides each read.
  return readOnly ? <BackofficeView {...props} /> : <TenantBackoffice {...props} />;
}

function TenantBackoffice(props: BackofficeProps) {
  const { t } = useTranslation();
  const access = useBackofficeAccess();
  if (access === "unknown") {
    return (
      <Page width="narrow">
        <Skeleton className="h-24 w-full" />
      </Page>
    );
  }
  if (access === "no") {
    return (
      <Page width="narrow">
        <EmptyState icon={LockKeyhole} title={t("placeholder.backoffice.title")} headingLevel={1}>
          {t("placeholder.backoffice.refused")}
        </EmptyState>
      </Page>
    );
  }
  return <BackofficeView {...props} />;
}

function BackofficeView({ section, projectId, settingsGroup }: BackofficeProps) {
  const { t } = useTranslation();
  const { base, readOnly } = useAdminApi();
  const path = (s: BackofficeSection) => `${base}/${s}`;
  if (section && !(readOnly && section === "support")) {
    return (
      <Page
        width={
          section === "reports" ||
          (section === "projects" && !projectId) ||
          section === "users" ||
          section === "memory"
            ? "wide"
            : "default"
        }
      >
        <nav aria-label={t("project.breadcrumb")} className="-mb-4 text-sm">
          <Link
            href={projectId ? path("projects") : base}
            className="text-fg-secondary underline-offset-4 hover:text-fg hover:underline"
          >
            {projectId ? t("governance.back") : t("backoffice.back")}
          </Link>
        </nav>
        {section === "projects" && projectId ? (
          <GovernedProjectView key={projectId} projectId={projectId} />
        ) : (
          <BackofficeSectionView section={section} settingsGroup={settingsGroup} />
        )}
      </Page>
    );
  }
  return (
    <Page width="narrow">
      <PageHeader
        title={readOnly ? t("console.support.backofficeTitle") : t("backoffice.title")}
        lead={readOnly ? t("console.support.backofficeLead") : t("backoffice.lead")}
      />
      <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface-raised">
        <SectionLink
          href={path("users")}
          icon={Users}
          title={t("users.title")}
          hint={t("users.hint")}
        />
        <SectionLink
          href={path("entities")}
          icon={FolderTree}
          title={t("entities.title")}
          hint={t("entities.hint")}
        />
        <SectionLink
          href={adminSettingsPath(SETTINGS_GROUPS[0], base)}
          icon={SlidersHorizontal}
          title={t("adminSettings.title")}
          hint={t("adminSettings.hint")}
        />
        <SectionLink
          href={path("signin")}
          icon={KeyRound}
          title={t("backoffice.signin")}
          hint={t("backoffice.signinHint")}
        />
        <SectionLink
          href={path("directory")}
          icon={Network}
          title={t("backoffice.directory")}
          hint={t("backoffice.directoryHint")}
        />
        <SectionLink
          href={path("reports")}
          icon={ChartColumn}
          title={t("reports.title")}
          hint={t("reports.hint")}
        />
        <SectionLink
          href={path("projects")}
          icon={FolderKanban}
          title={t("governance.title")}
          hint={t("governance.hint")}
        />
        <SectionLink
          href={path("residency")}
          icon={Earth}
          title={t("backoffice.residency")}
          hint={t("backoffice.residencyHint")}
        />
        <SectionLink
          href={path("models")}
          icon={Cpu}
          title={t("backoffice.models")}
          hint={t("backoffice.modelsHint")}
        />
        <SectionLink
          href={path("memory")}
          icon={NotebookPen}
          title={t("backoffice.memory")}
          hint={t("backoffice.memoryHint")}
        />
        <SectionLink
          href={path("branding")}
          icon={Palette}
          title={t("branding.title")}
          hint={t("branding.hint")}
        />
        <SectionLink
          href={path("plan")}
          icon={Gauge}
          title={t("plan.title")}
          hint={t("plan.hint")}
        />
        {readOnly ? null : (
          <SectionLink
            href={path("support")}
            icon={LifeBuoy}
            title={t("supportAccess.title")}
            hint={t("supportAccess.hint")}
          />
        )}
      </ul>
    </Page>
  );
}

function BackofficeSectionView({
  section,
  settingsGroup,
}: {
  section: BackofficeSection;
  settingsGroup?: SettingsGroup | undefined;
}) {
  switch (section) {
    case "users":
      return <UsersAdmin />;
    case "entities":
      return <EntitiesAdmin />;
    case "settings":
      return <SettingsAdmin group={settingsGroup ?? null} />;
    case "directory":
      return <DirectoryAdmin />;
    case "signin":
      return <SignInAdmin />;
    case "residency":
      return <ResidencyScreen />;
    case "models":
      return <ModelsScreen />;
    case "reports":
      return <Reports />;
    case "projects":
      return <GovernedProjects />;
    case "audit":
      return <AuditList />;
    case "plan":
      return <PlanSection />;
    case "memory":
      return <MemoryScreen />;
    case "branding":
      return <BrandingScreen />;
    case "support":
      return <SupportSection />;
  }
}

function SectionLink(props: { href: string; icon: LucideIcon; title: string; hint: string }) {
  const Icon = props.icon;
  return (
    <li>
      <Link
        href={props.href}
        className="flex items-center gap-3 px-4 py-3.5 transition-colors duration-(--duration-fast) outline-offset-[-2px] hover:bg-surface-hover"
      >
        <Icon aria-hidden="true" className="size-5 shrink-0 text-fg-muted" strokeWidth={1.5} />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-medium text-fg">{props.title}</span>
          <span className="text-sm text-fg-secondary">{props.hint}</span>
        </span>
        <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-fg-muted" />
      </Link>
    </li>
  );
}
